import React, { useEffect, useRef, useState } from "react";
import { Seo } from "@/components/Seo";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { imgSrc } from "@/lib/img";
import ReactMarkdown from "react-markdown";
import {
  ShieldCheck, Clock, XCircle, CheckCircle2, Link2, Mail, Plus, Trash2, Send, Star, Quote,
  Newspaper, ImagePlus, Search, Flag, Bold, List, Eye, Pencil, UserX, UserCheck, Sparkles,
  ShieldAlert, ScrollText, Download,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CollectivesQueue } from "@/components/admin/CollectivesQueue";
import { CollectivesHealth } from "@/components/admin/CollectivesHealth";
import { ReferrersQueue } from "@/components/admin/ReferrersQueue";
import { CampaignsPanel } from "@/components/admin/CampaignsPanel";
import { PerksPanel } from "@/components/admin/PerksPanel";
import { EmployerPanel } from "@/components/admin/EmployerPanel";
import { DigestCard } from "@/components/admin/DigestCard";
import AdminAnalytics from "@/components/admin/AdminAnalytics";
import { LaunchHealthPanel } from "@/components/admin/LaunchHealthPanel";
import { GrowthPanel } from "@/components/admin/GrowthPanel";
import { BroadcastCard } from "@/components/admin/BroadcastCard";
import { EraseUserDialog } from "@/components/admin/EraseUserDialog";
import { RoleBadgesPanel } from "@/components/admin/RoleBadgesPanel";
import { AdminOtpPanel } from "@/components/AdminOtpPanel";
import MapLeaflet from "@/components/MapLeaflet";
import { downloadCsv } from "@/lib/csv";
import { MapPinCheck, BarChart3, MessageSquareWarning } from "lucide-react";

function VerifiedNearbyChip({ userId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    api.adminVerificationNearby(userId).then((d) => alive && setData(d)).catch(() => alive && setData({ count: 0, sample: [] }));
    return () => { alive = false; };
  }, [userId]);
  if (!data) return null;
  if (data.count === 0) {
    return (
      <div data-testid={`verified-nearby-chip-${userId}`} className="text-[11px] text-neutral-400 italic mt-1 w-[200px] text-center">
        No verified orgs within 10 miles
      </div>
    );
  }
  return (
    <div className="w-[200px]">
      <button
        type="button"
        data-testid={`verified-nearby-chip-${userId}`}
        onClick={() => setOpen((v) => !v)}
        className="w-full mt-1 inline-flex items-center justify-center gap-1.5 text-[11px] font-medium bg-forest/10 text-forest border border-forest/25 rounded-full px-3 py-1 hover:bg-forest/15"
      >
        <MapPinCheck className="w-3 h-3" />
        {data.count} verified {data.count === 1 ? "charity" : "charities"} within 10 miles
      </button>
      {open && (
        <div data-testid={`verified-nearby-list-${userId}`} className="mt-2 bg-white border border-neutral-200 rounded-lg p-2 text-[11px] text-forest space-y-1">
          {data.sample.map((s) => (
            <div key={s.user_id} className="flex items-center justify-between gap-2">
              <span className="truncate">{s.name}</span>
              <span className="text-neutral-500 shrink-0">{s.distance_miles} mi</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const MD_COMPONENTS = {
  p: ({ node, ...props }) => <p className="text-neutral-700 text-sm leading-relaxed mb-3" {...props} />,
  li: ({ node, ...props }) => <li className="text-neutral-700 text-sm ml-5 list-disc" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold text-forest" {...props} />,
  img: ({ node, src, alt, ...props }) => <img src={imgSrc(src)} alt={alt || ""} className="rounded-lg my-3 max-h-56 object-cover" {...props} />,
};

function FeaturedSection() {
  const [opps, setOpps] = useState([]);
  const [state, setState] = useState("loading");

  const load = () => {
    setState("loading");
    api.adminOpportunities()
      .then((d) => { setOpps(d); setState("ready"); })
      .catch(() => setState("error"));
  };
  useEffect(() => { load(); }, []);

  const toggle = async (id) => {
    const target = !opps.find(o => o.id === id)?.featured;
    const res = await api.toggleFeatured(id, target);
    setOpps(list => list.map(o => o.id === id ? { ...o, featured: res.featured } : o));
    toast.success(res.featured ? "Featured on Explore" : "Removed from featured");
  };

  return (
    <div className="mt-14" data-testid="featured-section">
      <h2 className="font-display text-2xl text-forest tracking-tight flex items-center gap-2">
        <Star className="w-5 h-5" /> Featured opportunities
      </h2>
      <p className="text-neutral-600 text-sm mt-1">Starred roles appear in the "Featured opportunities" section on Explore.</p>
      <div className="mt-5 space-y-2">
        {state === "loading" && <div className="text-sm text-neutral-500">Loading roles…</div>}
        {state === "error" && (
          <div data-testid="featured-error" className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 flex items-center justify-between gap-3 flex-wrap">
            Couldn't load the roles — the server may have been busy.
            <Button data-testid="featured-retry-btn" size="sm" variant="outline" onClick={load} className="rounded-full h-8 text-xs">Retry</Button>
          </div>
        )}
        {state === "ready" && opps.length === 0 && (
          <div data-testid="featured-empty" className="text-sm text-neutral-500">No open roles to feature yet — they'll appear here as soon as organisations or hosts publish opportunities.</div>
        )}
        {opps.map(o => (
          <div key={o.id} data-testid={`featured-row-${o.id}`} className="bg-white border border-neutral-200 rounded-xl px-4 py-3 flex items-center gap-3">
            <button data-testid={`feature-toggle-${o.id}`} onClick={() => toggle(o.id)} aria-label="Toggle featured"
              className={`shrink-0 transition-colors ${o.featured ? "text-amber-500" : "text-neutral-300 hover:text-amber-400"}`}>
              <Star className={`w-5 h-5 ${o.featured ? "fill-amber-400" : ""}`} />
            </button>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-forest truncate">{o.title}</div>
              <div className="text-xs text-neutral-500">{o.org_name}{o.community_led ? " · Community-led" : ""}</div>
            </div>
            {o.featured && <Badge className="bg-amber-100 text-amber-700 border-none rounded-full shrink-0">Featured</Badge>}
          </div>
        ))}
      </div>
    </div>
  );
}

// Item 1 (2026-07): landing-story curation — admins star stories onto the
// landing page. The section there only renders once 3+ are featured.
function FeaturedStoriesSection() {
  const [stories, setStories] = useState([]);
  const [state, setState] = useState("loading");

  const load = () => {
    setState("loading");
    api.stories(100)
      .then((d) => { setStories(d); setState("ready"); })
      .catch(() => setState("error"));
  };
  useEffect(() => { load(); }, []);

  const featuredCount = stories.filter(s => s.featured).length;

  const toggle = async (id) => {
    const target = !stories.find(s => s.id === id)?.featured;
    try {
      const res = await api.adminFeatureStory(id, target);
      setStories(list => list.map(s => s.id === id ? { ...s, featured: res.featured } : s));
      toast.success(res.featured ? "Featured on the landing page" : "Removed from landing");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  return (
    <div className="mt-14" data-testid="featured-stories-section">
      <h2 className="font-display text-2xl text-forest tracking-tight flex items-center gap-2">
        <Quote className="w-5 h-5" /> Landing stories
      </h2>
      <p className="text-neutral-600 text-sm mt-1">
        The landing page shows ONLY starred stories — and only once <b>3 or more</b> are
        starred ({featuredCount} currently). Below that the section stays hidden.
      </p>
      <div className="mt-5 space-y-2">
        {state === "loading" && <div className="text-sm text-neutral-500">Loading stories…</div>}
        {state === "error" && (
          <div data-testid="featured-stories-error" className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 flex items-center justify-between gap-3 flex-wrap">
            Couldn't load stories — the server may have been busy.
            <Button data-testid="featured-stories-retry-btn" size="sm" variant="outline" onClick={load} className="rounded-full h-8 text-xs">Retry</Button>
          </div>
        )}
        {state === "ready" && stories.length === 0 && (
          <div data-testid="featured-stories-empty" className="text-sm text-neutral-500">No stories yet — they'll appear here as volunteers share them.</div>
        )}
        {stories.map(s => (
          <div key={s.id} data-testid={`featured-story-row-${s.id}`} className="bg-white border border-neutral-200 rounded-xl px-4 py-3 flex items-center gap-3">
            <button data-testid={`feature-story-toggle-${s.id}`} onClick={() => toggle(s.id)} aria-label="Toggle featured story"
              className={`shrink-0 transition-colors ${s.featured ? "text-amber-500" : "text-neutral-300 hover:text-amber-400"}`}>
              <Star className={`w-5 h-5 ${s.featured ? "fill-amber-400" : ""}`} />
            </button>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-forest truncate">"{s.text}"</div>
              <div className="text-xs text-neutral-500">{s.user_name}{s.org_name ? ` · ${s.org_name}` : ""}{s.hours ? ` · ${s.hours}h` : ""}</div>
            </div>
            {s.featured && <Badge className="bg-amber-100 text-amber-700 border-none rounded-full shrink-0">On landing</Badge>}
          </div>
        ))}
      </div>
    </div>
  );
}


function ContentStudio() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", content_type: "article", body: "", embed_url: "", author_name: "CivicHearth Team", cover_image: "" });
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const bodyRef = useRef(null);

  useEffect(() => { api.contentList().then(setItems).catch(() => {}); }, []);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const wrapBold = () => {
    const el = bodyRef.current;
    const s = el?.selectionStart ?? form.body.length;
    const e = el?.selectionEnd ?? form.body.length;
    const sel = form.body.slice(s, e) || "bold text";
    setForm(f => ({ ...f, body: f.body.slice(0, s) + `**${sel}**` + f.body.slice(e) }));
  };

  const addBullets = () => {
    const el = bodyRef.current;
    const s = el?.selectionStart ?? form.body.length;
    const e = el?.selectionEnd ?? form.body.length;
    const sel = form.body.slice(s, e);
    const block = sel
      ? sel.split("\n").map(l => (l.trim() ? `- ${l.trim()}` : l)).join("\n")
      : "\n- First point\n- Second point\n";
    setForm(f => ({ ...f, body: f.body.slice(0, s) + block + f.body.slice(e) }));
  };

  const onInlineImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const res = await api.uploadImage(f);
      setForm(fm => ({ ...fm, body: `${fm.body}\n\n![image](${res.url})\n` }));
      toast.success("Image inserted into the article");
    } catch { toast.error("Upload failed"); }
  };

  const onCover = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const res = await api.uploadImage(f);
      setForm(fm => ({ ...fm, cover_image: res.url }));
      toast.success("Cover uploaded");
    } catch { toast.error("Upload failed"); }
  };

  const publish = async () => {
    if (!form.title.trim() || !form.body.trim()) return toast.error("Title and body are required");
    setSaving(true);
    try {
      const created = await api.adminCreateContent(form);
      setItems(list => [created, ...list]);
      setForm({ title: "", content_type: "article", body: "", embed_url: "", author_name: "CivicHearth Team", cover_image: "" });
      toast.success("Published to Community");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not publish");
    }
    setSaving(false);
  };

  const remove = async (id) => {
    await api.adminDeleteContent(id);
    setItems(list => list.filter(i => i.id !== id));
    toast("Removed");
  };

  const feature = async (id) => {
    try {
      const target = !items.find(c => c.id === id)?.featured;
      const res = await api.toggleContentFeatured(id, target);
      setItems(list => list.map(c => c.id === id ? { ...c, featured: res.featured } : c));
      toast.success(res.featured ? "Featured on the Community hub" : "Removed from Featured");
    } catch { toast.error("Could not update"); }
  };

  return (
    <div className="mt-14" data-testid="content-studio">
      <h2 className="font-display text-2xl text-forest tracking-tight flex items-center gap-2">
        <Newspaper className="w-5 h-5" /> Content studio
      </h2>
      <p className="text-neutral-600 text-sm mt-1">Publish interviews, podcast episodes and articles to the Community hub.</p>

      <Card className="mt-5 border-neutral-200">
        <CardContent className="p-6 space-y-3">
          <div className="flex gap-3 flex-wrap">
            <Input data-testid="content-title" placeholder="Title" value={form.title} onChange={set("title")} className="h-10 flex-1 min-w-[220px]" />
            <Select value={form.content_type} onValueChange={(v) => setForm(f => ({ ...f, content_type: v }))}>
              <SelectTrigger data-testid="content-type-select" className="w-36 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="article">Article</SelectItem>
                <SelectItem value="interview">Interview</SelectItem>
                <SelectItem value="podcast">Podcast</SelectItem>
                <SelectItem value="video">Video</SelectItem>
              </SelectContent>
            </Select>
            <Input data-testid="content-author" placeholder="Author" value={form.author_name} onChange={set("author_name")} className="h-10 w-44" />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button data-testid="md-bold-btn" onClick={wrapBold} title="Bold (wraps selection)"
              className="w-8 h-8 rounded-lg border border-neutral-200 flex items-center justify-center text-neutral-600 hover:border-forest hover:text-forest transition-colors">
              <Bold className="w-4 h-4" />
            </button>
            <button data-testid="md-bullets-btn" onClick={addBullets} title="Bullet list"
              className="w-8 h-8 rounded-lg border border-neutral-200 flex items-center justify-center text-neutral-600 hover:border-forest hover:text-forest transition-colors">
              <List className="w-4 h-4" />
            </button>
            <label title="Insert image into body" className="w-8 h-8 rounded-lg border border-neutral-200 flex items-center justify-center text-neutral-600 hover:border-forest hover:text-forest transition-colors cursor-pointer">
              <ImagePlus className="w-4 h-4" />
              <input data-testid="md-image-input" type="file" accept="image/*" className="hidden" onChange={onInlineImage} />
            </label>
            <button data-testid="md-preview-btn" onClick={() => setPreviewing(p => !p)}
              className={`h-8 px-3 rounded-lg border text-xs inline-flex items-center gap-1.5 transition-colors ${previewing ? "bg-forest text-white border-forest" : "border-neutral-200 text-neutral-600 hover:border-forest hover:text-forest"}`}>
              {previewing ? <Pencil className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} {previewing ? "Edit" : "Preview"}
            </button>
            <span className="text-[11px] text-neutral-400 ml-1">Markdown: **bold**, - bullets, images</span>
          </div>
          {previewing ? (
            <div data-testid="content-preview" className="border border-neutral-200 rounded-lg p-4 min-h-[120px] bg-neutral-50/50">
              {form.body.trim()
                ? <ReactMarkdown components={MD_COMPONENTS}>{form.body}</ReactMarkdown>
                : <span className="text-sm text-neutral-400">Nothing to preview yet.</span>}
            </div>
          ) : (
            <Textarea ref={bodyRef} data-testid="content-body" rows={6} placeholder="Body — supports markdown: **bold**, - bullet lists, blank lines for paragraphs. Use the toolbar to insert images." value={form.body} onChange={set("body")} />
          )}
          <div className="flex gap-3 items-center flex-wrap">
            <Input data-testid="content-embed" placeholder="Embed URL (YouTube / Spotify) — optional" value={form.embed_url} onChange={set("embed_url")} className="h-10 flex-1 min-w-[220px]" />
            <label className="inline-flex items-center gap-2 text-sm text-forest cursor-pointer hover:underline">
              <ImagePlus className="w-4 h-4" /> {form.cover_image ? "Change cover" : "Add cover"}
              <input data-testid="content-cover-input" type="file" accept="image/*" className="hidden" onChange={onCover} />
            </label>
            <Button data-testid="content-publish-btn" onClick={publish} disabled={saving} className="rounded-full bg-forest hover:bg-forest-hover h-10 px-5">
              Publish
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="mt-5 space-y-2">
        {items.map(c => (
          <div key={c.id} data-testid={`content-row-${c.id}`} className="bg-white border border-neutral-200 rounded-xl px-4 py-3 flex items-center gap-3">
            <Badge className="rounded-full text-[10px] uppercase bg-terracotta/10 text-terracotta border-none shrink-0">{c.content_type}</Badge>
            <div className="flex-1 min-w-0 font-medium text-forest truncate">{c.title}</div>
            <span className="text-xs text-neutral-400 shrink-0">{new Date(c.created_at).toLocaleDateString()}</span>
            <button data-testid={`content-feature-${c.id}`} onClick={() => feature(c.id)} className="shrink-0" aria-label="Feature content">
              <Star className={`w-4 h-4 transition-colors ${c.featured ? "fill-amber-400 text-amber-400" : "text-neutral-300 hover:text-amber-400"}`} />
            </button>
            <button data-testid={`content-delete-${c.id}`} onClick={() => remove(c.id)} className="text-neutral-400 hover:text-red-500 shrink-0" aria-label="Delete content">
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const emailTemplate = (code) => `Subject: An invitation to join CivicHearth as a founding cause

Hi there,

I'm reaching out because I think your organisation would be a brilliant fit for CivicHearth — a platform that connects volunteers with causes based on shared values and skills.

As a partner you get:
• Volunteers matched to your needs (values, skills, location)
• One-click hour approvals, in-platform messaging and impact analytics
• A verified badge and a public impact page for your cause

It's free for causes, and applying takes under a minute. You'll be fast-tracked through verification:

${window.location.origin}/partners?ref=${code}

Would love to see you on board.

Warm regards`;

function OutreachSection() {
  const [invites, setInvites] = useState([]);
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);

  const load = () => api.adminInvites().then(setInvites).catch(() => setInvites([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!label.trim()) return toast.error("Give the invite a label, e.g. a campaign or contact name");
    setCreating(true);
    try {
      await api.createInvite(label);
      setLabel("");
      await load();
      toast.success("Invite link created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create invite");
    }
    setCreating(false);
  };

  const [preview, setPreview] = useState(null);

  const copy = async (text, msg, title) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(msg);
    } catch {
      setPreview({ title, text });
    }
  };

  const manualCopy = () => {
    const el = document.getElementById("copy-preview-textarea");
    if (el) {
      el.focus();
      el.select();
      try {
        document.execCommand("copy");
        toast.success("Copied");
      } catch {
        toast("Press Ctrl/Cmd + C to copy the selected text");
      }
    }
  };

  const remove = async (id) => {
    await api.deleteInvite(id);
    setInvites(list => list.filter(i => i.id !== id));
    toast("Invite removed");
  };

  return (
    <div className="mt-14" data-testid="outreach-section">
      <h2 className="font-display text-2xl text-forest tracking-tight flex items-center gap-2">
        <Send className="w-5 h-5" /> Partner outreach
      </h2>
      <p className="text-neutral-600 text-sm mt-1">Create tracked invite links for your pre-launch outreach. Applications made via a link are attributed to it.</p>

      <div className="flex gap-2 mt-5 max-w-md">
        <Input
          data-testid="invite-label-input"
          placeholder="Campaign or contact, e.g. 'Bristol food banks'"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && create()}
          className="h-10"
        />
        <Button data-testid="invite-create-btn" onClick={create} disabled={creating} className="rounded-full bg-forest hover:bg-forest-hover h-10 px-4 shrink-0">
          <Plus className="w-4 h-4 mr-1" /> Create
        </Button>
      </div>

      <div className="mt-5 space-y-3">
        {invites.length === 0 && <div className="text-sm text-neutral-500">No invite links yet.</div>}
        {invites.map(inv => (
          <div key={inv.id} data-testid={`invite-row-${inv.code}`} className="bg-white border border-neutral-200 rounded-xl p-4 flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[180px]">
              <div className="font-medium text-forest">{inv.label}</div>
              <div className="text-xs text-neutral-500 mt-0.5 font-mono">/partners?ref={inv.code}</div>
            </div>
            <div className="flex items-center gap-4 text-center">
              <div>
                <div className="font-display text-xl text-forest leading-none" data-testid={`invite-apps-${inv.code}`}>{inv.applications}</div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 mt-1">applied</div>
              </div>
              <div>
                <div className="font-display text-xl text-forest leading-none">{inv.verified}</div>
                <div className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 mt-1">verified</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button data-testid={`invite-copy-link-${inv.code}`} variant="outline" size="sm" className="rounded-full h-8 text-xs"
                onClick={() => copy(`${window.location.origin}/partners?ref=${inv.code}`, "Invite link copied", "Invite link")}>
                <Link2 className="w-3.5 h-3.5 mr-1" /> Copy link
              </Button>
              <Button data-testid={`invite-copy-email-${inv.code}`} variant="outline" size="sm" className="rounded-full h-8 text-xs"
                onClick={() => copy(emailTemplate(inv.code), "Email template copied", "Email template")}>
                <Mail className="w-3.5 h-3.5 mr-1" /> Copy email
              </Button>
              <button data-testid={`invite-delete-${inv.code}`} onClick={() => remove(inv.id)} className="text-neutral-400 hover:text-red-500" aria-label="Delete invite">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent data-testid="copy-preview-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{preview?.title}</DialogTitle>
            <DialogDescription>Your browser blocked automatic copying — here's the content. Use the button below or select it manually.</DialogDescription>
          </DialogHeader>
          <textarea
            id="copy-preview-textarea"
            data-testid="copy-preview-textarea"
            readOnly
            value={preview?.text || ""}
            onFocus={(e) => e.target.select()}
            className="w-full h-56 text-sm border border-neutral-200 rounded-lg p-3 font-mono bg-neutral-50 focus:outline-none"
          />
          <div className="flex justify-end">
            <Button data-testid="copy-preview-btn" onClick={manualCopy} className="rounded-full bg-forest hover:bg-forest-hover">Select & copy</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const VERIFY_REJECT_REASONS = [
  "Couldn't verify charity/company registration",
  "Website missing or doesn't match",
  "Email domain doesn't match organisation",
  "Insufficient information provided",
  "Other",
];

function AdminSearchSection() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const timer = useRef();

  const run = (val) => {
    clearTimeout(timer.current);
    if (val.trim().length < 2) { setResults(null); return; }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try { setResults(await api.adminSearch(val.trim())); } catch { setResults(null); }
      setSearching(false);
    }, 350);
  };

  const ban = async (u) => {
    try {
      const res = await api.toggleBan(u.user_id);
      setResults(r => ({ ...r, users: r.users.map(x => x.user_id === u.user_id ? { ...x, banned: res.banned } : x) }));
      toast[res.banned ? "success" : "message"](res.banned ? `${u.name} banned — sessions revoked` : `${u.name} unbanned`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  // Phase 14 — platform admin promote/demote with last-admin guard server-side.
  const toggleAdmin = async (u) => {
    const promoting = !u.is_admin;
    const label = u.org_name || u.name;
    if (!window.confirm(promoting
      ? `Make ${label} a platform admin? They'll verify with an email code before admin tools unlock.`
      : `Remove ${label}'s platform admin access?`)) return;
    try {
      const res = promoting ? await api.promoteAdmin(u.user_id) : await api.demoteAdmin(u.user_id);
      setResults(r => ({ ...r, users: r.users.map(x => x.user_id === u.user_id ? { ...x, is_admin: res.is_admin } : x) }));
      toast.success(promoting ? `${label} is now a platform admin` : `${label} is no longer an admin`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update admin status"); }
  };

  return (
    <div className="mt-14" data-testid="admin-search-section">
      <h2 className="font-display text-2xl text-forest tracking-tight flex items-center gap-2">
        <Search className="w-5 h-5" /> Search users, organisations & teams
      </h2>
      <div className="relative max-w-md mt-5">
        <Input
          data-testid="admin-search-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); run(e.target.value); }}
          placeholder="Name, email or team…"
          className="h-10"
        />
        {searching && <span className="absolute right-3 top-2.5 text-xs text-neutral-400">…</span>}
      </div>
      {results && (
        <div className="mt-4 space-y-2" data-testid="admin-search-results">
          {results.users.length === 0 && results.teams.length === 0 && (
            <div className="text-sm text-neutral-500">No matches.</div>
          )}
          {results.users.map(u => (
            <div key={u.user_id} data-testid={`search-user-${u.user_id}`} className="bg-white border border-neutral-200 rounded-xl px-4 py-3 flex items-center gap-3">
              <Avatar className="w-8 h-8 shrink-0">
                <AvatarImage src={imgSrc(u.picture)} alt={u.name} />
                <AvatarFallback className="bg-forest text-white text-xs">{u.name?.[0]}</AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <Link to={u.user_type === "organisation" ? `/o/${u.user_id}` : `/u/${u.user_id}`} className="font-medium text-forest text-sm hover:underline">
                  {u.org_name || u.name}
                </Link>
                <div className="text-xs text-neutral-500 truncate">{u.email} · {u.user_type || "individual"}{u.location ? ` · ${u.location}` : ""}</div>
              </div>
              {u.verified && <Badge className="rounded-full text-[10px] bg-forest/10 text-forest border-none shrink-0">Verified</Badge>}
              {u.is_admin && <Badge className="rounded-full text-[10px] bg-amber-100 text-amber-700 border-none shrink-0">Admin</Badge>}
              {u.banned && <Badge className="rounded-full text-[10px] bg-red-100 text-red-700 border-none shrink-0">Banned</Badge>}
              <button data-testid={`admin-toggle-${u.user_id}`} onClick={() => toggleAdmin(u)}
                className={`text-xs px-2.5 py-1.5 rounded-full border inline-flex items-center gap-1 transition-colors shrink-0 ${u.is_admin ? "border-amber-300 text-amber-700 hover:bg-amber-50" : "border-forest/30 text-forest hover:bg-forest/5"}`}>
                {u.is_admin ? <><ShieldAlert className="w-3 h-3" /> Remove admin</> : <><ShieldCheck className="w-3 h-3" /> Make admin</>}
              </button>
              <button data-testid={`ban-toggle-${u.user_id}`} onClick={() => ban(u)}
                className={`text-xs px-2.5 py-1.5 rounded-full border inline-flex items-center gap-1 transition-colors shrink-0 ${u.banned ? "border-forest/30 text-forest hover:bg-forest/5" : "border-red-200 text-red-600 hover:bg-red-50"}`}>
                {u.banned ? <><UserCheck className="w-3 h-3" /> Unban</> : <><UserX className="w-3 h-3" /> Ban</>}
              </button>
              <EraseUserDialog
                user={u}
                onErased={(uid) => setResults(r => ({ ...r, users: r.users.filter(x => x.user_id !== uid) }))}
              />
            </div>
          ))}
          {results.teams.map(t => (
            <div key={t.id} data-testid={`search-team-${t.id}`} className="bg-white border border-neutral-200 rounded-xl px-4 py-3 flex items-center gap-3">
              <Badge className="rounded-full text-[10px] uppercase bg-terracotta/10 text-terracotta border-none shrink-0">Team</Badge>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-forest text-sm">{t.name}</div>
                <div className="text-xs text-neutral-500">Led by {t.owner_name} · {t.member_count} member{t.member_count !== 1 ? "s" : ""}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReportsSection() {
  const [reports, setReports] = useState([]);
  const [showAll, setShowAll] = useState(false);

  const load = (all = showAll) => api.adminReports(all ? "all" : "open").then(setReports).catch(() => {});
  useEffect(() => { load(); }, [showAll]); // eslint-disable-line react-hooks/exhaustive-deps -- render-scoped fetch helper; effect keys on its real inputs

  const resolve = async (r, action) => {
    try {
      await api.resolveReport(r.id, action);
      toast.success(action === "dismiss" ? "Report dismissed" : action === "remove_content" ? "Content removed" : "User banned");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not resolve"); }
  };

  return (
    <div className="mt-14" data-testid="reports-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-2xl text-forest tracking-tight flex items-center gap-2">
          <Flag className="w-5 h-5" /> Reports {reports.filter(r => r.status === "open").length > 0 && (
            <Badge className="rounded-full bg-red-100 text-red-700 border-none">{reports.filter(r => r.status === "open").length} open</Badge>
          )}
        </h2>
        <button data-testid="reports-toggle-all" onClick={() => setShowAll(s => !s)} className="text-xs text-neutral-500 hover:text-forest underline">
          {showAll ? "Show open only" : "Show all (incl. resolved)"}
        </button>
      </div>
      <p className="text-neutral-600 text-sm mt-1">User-flagged content. Dismiss, remove the content, or ban the user responsible.</p>
      <div className="mt-5 space-y-3">
        {reports.length === 0 && <div className="text-sm text-neutral-500">No {showAll ? "" : "open "}reports. All clear.</div>}
        {reports.map(r => (
          <div key={r.id} data-testid={`report-row-${r.id}`} className="bg-white border border-neutral-200 rounded-xl p-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="rounded-full text-[10px] uppercase bg-terracotta/10 text-terracotta border-none">{(r.target_type || "").replace(/_/g, " ")}</Badge>
              <Badge variant="outline" className="rounded-full text-[10px]">{r.reason}</Badge>
              {r.status !== "open" && <Badge className="rounded-full text-[10px] bg-neutral-200 text-neutral-600 border-none">{r.status}{r.resolution ? ` · ${r.resolution.replace("_", " ")}` : ""}</Badge>}
              <span className="text-xs text-neutral-400 ml-auto">{new Date(r.created_at).toLocaleDateString()}</span>
            </div>
            <div className="text-sm text-neutral-700 mt-2">"{r.target_preview}"</div>
            {r.context && (r.context.collective_id || r.context.opportunity_id) && (
              <div className="text-xs mt-1 flex gap-3 items-center">
                {r.context.collective_id && (
                  <Link data-testid={`report-ctx-collective-${r.id}`} to={`/collectives/${r.context.collective_id}`} className="text-forest underline underline-offset-2">
                    View collective
                  </Link>
                )}
                {r.context.opportunity_id && (
                  <Link data-testid={`report-ctx-opportunity-${r.id}`} to={`/opportunities/${r.context.opportunity_id}`} className="text-forest underline underline-offset-2">
                    View event
                  </Link>
                )}
                {r.context.room_id && <span className="text-neutral-400">room: {r.context.room_id}</span>}
              </div>
            )}
            <div className="text-xs text-neutral-500 mt-1">
              By {r.offender_name || "unknown"} · Reported by {r.reporter_name}{r.note ? ` — "${r.note}"` : ""}
            </div>
            {r.status === "open" && (
              <div className="flex gap-2 mt-3 flex-wrap">
                <Button data-testid={`report-dismiss-${r.id}`} onClick={() => resolve(r, "dismiss")} variant="outline" size="sm" className="rounded-full h-8 text-xs">Dismiss</Button>
                {r.target_type !== "user" && (
                  <Button data-testid={`report-remove-${r.id}`} onClick={() => resolve(r, "remove_content")} variant="outline" size="sm" className="rounded-full h-8 text-xs border-amber-300 text-amber-700 hover:bg-amber-50">
                    Remove content
                  </Button>
                )}
                <Button data-testid={`report-ban-${r.id}`} onClick={() => resolve(r, "ban_user")} variant="outline" size="sm" className="rounded-full h-8 text-xs border-red-200 text-red-600 hover:bg-red-50">
                  <UserX className="w-3 h-3 mr-1" /> Ban user
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// F&F test prep: product feedback from the floating site widget. Separate
// from volunteer↔org feedback (different collection + purpose).
function SiteFeedbackSection() {
  const [data, setData] = useState({ items: [], counts: { new: 0, reviewed: 0, resolved: 0 } });
  const [category, setCategory] = useState("all");
  const [showResolved, setShowResolved] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = () => {
    const params = {};
    if (category !== "all") params.category = category;
    api.adminSiteFeedback(params).then(setData).catch(() => {});
  };
  useEffect(() => { load(); }, [category]); // eslint-disable-line react-hooks/exhaustive-deps -- render-scoped fetch helper; effect keys on its real inputs

  const setStatus = async (f, status) => {
    setBusy(f.id);
    try {
      await api.resolveSiteFeedback(f.id, status);
      toast.success(status === "resolved" ? "Marked resolved" : "Marked reviewed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
    setBusy(null);
  };

  const visible = data.items.filter(f => showResolved || f.status !== "resolved");
  // legacy "confusing" entries (pre-rename) display as "feedback"
  const catLabel = (c) => (c === "confusing" ? "feedback" : c);
  const CAT_STYLE = { bug: "bg-red-100 text-red-700", feedback: "bg-amber-100 text-amber-700", confusing: "bg-amber-100 text-amber-700", idea: "bg-sky-100 text-sky-700", other: "bg-neutral-200 text-neutral-600" };

  return (
    <div className="mt-14" data-testid="site-feedback-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-2xl text-forest tracking-tight flex items-center gap-2">
          <MessageSquareWarning className="w-5 h-5" /> Site feedback {data.counts.new > 0 && (
            <Badge data-testid="site-feedback-new-count" className="rounded-full bg-red-100 text-red-700 border-none">{data.counts.new} new</Badge>
          )}
        </h2>
        <div className="flex items-center gap-3">
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="site-feedback-category-filter" className="w-[150px] rounded-full h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              <SelectItem value="bug">Bug</SelectItem>
              <SelectItem value="feedback">Feedback</SelectItem>
              <SelectItem value="idea">Idea</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
          <button data-testid="site-feedback-toggle-resolved" onClick={() => setShowResolved(s => !s)} className="text-xs text-neutral-500 hover:text-forest underline">
            {showResolved ? "Hide resolved" : "Show resolved"}
          </button>
        </div>
      </div>
      <p className="text-neutral-600 text-sm mt-1">Notes from the floating "Something off?" widget — bugs, confusion and ideas from testers.</p>
      <div className="mt-5 space-y-3">
        {visible.length === 0 && <div className="text-sm text-neutral-500">Nothing here — all quiet.</div>}
        {visible.map(f => (
          <div key={f.id} data-testid={`site-feedback-row-${f.id}`} className="bg-white border border-neutral-200 rounded-xl p-4">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={`rounded-full text-[10px] uppercase border-none ${CAT_STYLE[f.category] || CAT_STYLE.other}`}>{catLabel(f.category)}</Badge>
              {f.status !== "new" && <Badge className="rounded-full text-[10px] bg-neutral-200 text-neutral-600 border-none">{f.status}</Badge>}
              <span className="text-xs text-neutral-400 ml-auto">{new Date(f.created_at).toLocaleString()}</span>
            </div>
            <div className="text-sm text-neutral-700 mt-2 whitespace-pre-line">{f.message}</div>
            <div className="text-xs text-neutral-500 mt-1.5">
              {f.user_name ? `${f.user_name}` : f.email ? `Anonymous · ${f.email}` : "Anonymous"} · on <span className="font-mono">{f.path || "?"}</span>{f.viewport ? ` · ${f.viewport}` : ""}
            </div>
            {f.status !== "resolved" && (
              <div className="flex gap-2 mt-3">
                {f.status === "new" && (
                  <Button data-testid={`site-feedback-review-${f.id}`} disabled={busy === f.id} onClick={() => setStatus(f, "reviewed")} variant="outline" size="sm" className="rounded-full h-8 text-xs">
                    Mark reviewed
                  </Button>
                )}
                <Button data-testid={`site-feedback-resolve-${f.id}`} disabled={busy === f.id} onClick={() => setStatus(f, "resolved")} variant="outline" size="sm" className="rounded-full h-8 text-xs border-forest/30 text-forest hover:bg-forest/5">
                  Mark resolved
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Phase 7: community-suggested custom skills awaiting review. Approve = joins
// the global taxonomy; reject = suggester keeps it as a personal skill only.
function SkillSuggestionsSection() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = () => api.adminSkillSuggestions("pending").then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (id, action) => {
    setBusy(id);
    try {
      const r = await api.resolveSkillSuggestion(id, action);
      toast.success(action === "approve"
        ? `"${r.skill}" added to the global skills list`
        : "Rejected — the volunteer keeps it as a personal skill");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update suggestion");
    } finally { setBusy(null); }
  };

  return (
    <div className="mt-14" data-testid="skill-suggestions-section">
      <h2 className="font-display text-2xl text-forest tracking-tight flex items-center gap-2">
        <Sparkles className="w-5 h-5" /> Suggested skills {items.length > 0 && (
          <Badge className="rounded-full bg-terracotta/10 text-terracotta border-none">{items.length} pending</Badge>
        )}
      </h2>
      <p className="text-neutral-600 text-sm mt-1">
        Custom skills volunteers added to their profiles. Approving adds them to the global taxonomy; rejecting leaves them as personal skills.
      </p>
      <div className="mt-5 space-y-3">
        {items.length === 0 && <div className="text-sm text-neutral-500">No pending suggestions.</div>}
        {items.map(s => (
          <div key={s.id} data-testid={`skill-suggestion-row-${s.id}`} className="bg-white border border-neutral-200 rounded-xl p-4 flex items-center gap-3 flex-wrap">
            <span className="font-medium text-forest capitalize">{s.skill}</span>
            <span className="text-xs text-neutral-500">by {s.suggested_by_name} · {new Date(s.created_at).toLocaleDateString()}</span>
            <div className="flex gap-2 ml-auto">
              <Button data-testid={`skill-approve-${s.id}`} disabled={busy === s.id} onClick={() => act(s.id, "approve")} size="sm" className="rounded-full h-8 text-xs bg-forest hover:bg-forest-hover">
                Approve
              </Button>
              <Button data-testid={`skill-reject-${s.id}`} disabled={busy === s.id} onClick={() => act(s.id, "reject")} variant="outline" size="sm" className="rounded-full h-8 text-xs border-red-200 text-red-600 hover:bg-red-50">
                Reject
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Phase 8: suspension appeals queue — approve unbans (user notified),
// reject keeps the ban with an optional reason shown on the suspended page.
function AppealsSection() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const [rejecting, setRejecting] = useState(null); // appeal id with reason input open
  const [reason, setReason] = useState("");

  const load = () => api.adminAppeals("pending").then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (id, action, why) => {
    setBusy(id);
    try {
      await api.resolveAppeal(id, action, why);
      toast.success(action === "approve" ? "Suspension lifted — the user has been notified" : "Appeal rejected");
      setRejecting(null); setReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resolve appeal");
    } finally { setBusy(null); }
  };

  return (
    <div className="mt-14" data-testid="appeals-section">
      <h2 className="font-display text-2xl text-forest tracking-tight flex items-center gap-2">
        <ShieldAlert className="w-5 h-5" /> Appeals {items.length > 0 && (
          <Badge className="rounded-full bg-terracotta/10 text-terracotta border-none">{items.length} pending</Badge>
        )}
      </h2>
      <p className="text-neutral-600 text-sm mt-1">Suspended users appealing their ban. Approving restores their account immediately.</p>
      <div className="mt-5 space-y-3">
        {items.length === 0 && <div className="text-sm text-neutral-500">No pending appeals.</div>}
        {items.map(a => (
          <div key={a.id} data-testid={`appeal-row-${a.id}`} className="bg-white border border-neutral-200 rounded-xl p-5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-forest">{a.user_name || a.user_email}</span>
              <span className="text-xs text-neutral-500">{a.user_email} · {new Date(a.created_at).toLocaleString()}</span>
            </div>
            <p className="text-sm text-neutral-700 mt-2 whitespace-pre-line">{a.text}</p>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <Button data-testid={`appeal-approve-${a.id}`} disabled={busy === a.id} onClick={() => act(a.id, "approve")} size="sm" className="rounded-full h-8 text-xs bg-forest hover:bg-forest-hover">
                Approve & unban
              </Button>
              {rejecting === a.id ? (
                <>
                  <Input
                    data-testid={`appeal-reject-reason-${a.id}`}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Optional reason shown to the user"
                    className="h-8 text-xs w-64"
                  />
                  <Button data-testid={`appeal-reject-confirm-${a.id}`} disabled={busy === a.id} onClick={() => act(a.id, "reject", reason)} variant="outline" size="sm" className="rounded-full h-8 text-xs border-red-200 text-red-600 hover:bg-red-50">
                    Confirm reject
                  </Button>
                  <button className="text-xs text-neutral-400 hover:text-neutral-600" onClick={() => { setRejecting(null); setReason(""); }}>Cancel</button>
                </>
              ) : (
                <Button data-testid={`appeal-reject-${a.id}`} onClick={() => { setRejecting(a.id); setReason(""); }} variant="outline" size="sm" className="rounded-full h-8 text-xs border-red-200 text-red-600 hover:bg-red-50">
                  Reject…
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Phase 8: append-only audit log of privileged actions (no backfill —
// records exist from Phase 8 onward). Filter by action + date range.
function AuditLogSection() {
  const [data, setData] = useState({ items: [], total: 0, actions: [] });
  const [action, setAction] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [skip, setSkip] = useState(0);
  const [exporting, setExporting] = useState(false);
  const LIMIT = 25;

  useEffect(() => {
    api.auditLogs({ action, date_from: dateFrom, date_to: dateTo, skip: 0, limit: LIMIT })
      .then((d) => { setData(d); setSkip(0); })
      .catch(() => {});
  }, [action, dateFrom, dateTo]);

  const loadMore = async () => {
    const next = skip + LIMIT;
    const d = await api.auditLogs({ action, date_from: dateFrom, date_to: dateTo, skip: next, limit: LIMIT });
    setData((prev) => ({ ...d, items: [...prev.items, ...d.items] }));
    setSkip(next);
  };

  // Client-side CSV export (same pattern as the analytics tab) — respects
  // the CURRENT filters by paging through /admin/audit-logs (server caps
  // 100/page) until every matching row is fetched. 5000-row safety cap.
  const exportCsv = async () => {
    setExporting(true);
    try {
      const rows = [];
      let offset = 0, total = Infinity;
      while (offset < total && rows.length < 5000) {
        const d = await api.auditLogs({ action, date_from: dateFrom, date_to: dateTo, skip: offset, limit: 100 });
        total = d.total;
        if (!d.items.length) break;
        rows.push(...d.items.map((l) => ({
          created_at: l.created_at,
          action: l.action,
          target_label: l.target_label,
          target_id: l.target_id,
          actor: l.actor_name,
          actor_id: l.actor_id,
          reason: l.metadata?.reason || "",
        })));
        offset += 100;
      }
      const today = new Date().toISOString().slice(0, 10);
      const parts = ["civichearth-audit"];
      if (action) parts.push(action.replace(/\./g, "-"));
      if (dateFrom || dateTo) parts.push(`${dateFrom || "start"}_to_${dateTo || today}`);
      downloadCsv(`${parts.join("-")}-${today}.csv`, rows,
        ["created_at", "action", "target_label", "target_id", "actor", "actor_id", "reason"]);
      toast.success(`Exported ${rows.length} audit ${rows.length === 1 ? "entry" : "entries"}`);
    } catch {
      toast.error("Export failed — try again");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="mt-14" data-testid="audit-log-section">
      <h2 className="font-display text-2xl text-forest tracking-tight flex items-center gap-2">
        <ScrollText className="w-5 h-5" /> Audit log
        <span className="text-sm font-sans text-neutral-500 font-normal">({data.total})</span>
      </h2>
      <p className="text-neutral-600 text-sm mt-1">Privileged actions, recorded from Phase 8 onward (earlier actions are not backfilled).</p>
      <div className="mt-4 flex items-end gap-3 flex-wrap">
        <div>
          <label className="text-[10px] uppercase tracking-wide text-neutral-500 font-bold block mb-1">Action</label>
          <select
            data-testid="audit-filter-action"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className="h-9 text-sm border border-neutral-200 rounded-lg px-2 bg-white"
          >
            <option value="">All actions</option>
            {(data.actions || []).map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide text-neutral-500 font-bold block mb-1">From</label>
          <Input data-testid="audit-filter-from" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 text-sm w-40" />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wide text-neutral-500 font-bold block mb-1">To</label>
          <Input data-testid="audit-filter-to" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 text-sm w-40" />
        </div>
        <button
          data-testid="audit-export-csv"
          onClick={exportCsv}
          disabled={exporting || data.total === 0}
          className={`ml-auto h-9 inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-4 border transition-colors ${
            exporting || data.total === 0
              ? "border-neutral-200 text-neutral-300 cursor-not-allowed"
              : "border-forest/20 text-forest hover:bg-forest/5"
          }`}
        >
          <Download className="w-3.5 h-3.5" />
          {exporting ? "Exporting…" : `Export CSV (${data.total})`}
        </button>
      </div>
      <div className="mt-4 space-y-2">
        {data.items.length === 0 && <div className="text-sm text-neutral-500">No audit entries match.</div>}
        {data.items.map(l => (
          <div key={l.id} data-testid={`audit-row-${l.id}`} className="bg-white border border-neutral-200 rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap text-sm">
            <span className="text-xs text-neutral-400 w-36 shrink-0">{new Date(l.created_at).toLocaleString()}</span>
            <Badge className="rounded-full bg-forest/10 text-forest border-none text-[10px]">{l.action}</Badge>
            <span className="text-neutral-700 min-w-0 truncate">{l.target_label}</span>
            {l.metadata?.reason ? <span className="text-xs text-neutral-500 truncate">"{l.metadata.reason}"</span> : null}
            <span className="text-xs text-neutral-400 ml-auto shrink-0">by {l.actor_name}</span>
          </div>
        ))}
      </div>
      {data.items.length < data.total && (
        <div className="flex justify-center mt-4">
          <button data-testid="audit-load-more" onClick={loadMore} className="text-sm text-forest border border-forest/20 rounded-full px-5 py-2 hover:bg-forest/5 transition-colors">
            Load more ({data.total - data.items.length} more)
          </button>
        </div>
      )}
    </div>
  );
}

function ReviewStat({ label, value, testId }) {
  return (
    <div data-testid={testId} className="bg-forest/5 border border-forest/10 rounded-lg px-3 py-2 text-center">
      <div className="font-display text-lg text-forest leading-tight">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.14em] text-forest/70 font-bold mt-0.5">{label}</div>
    </div>
  );
}

function VerificationReviewDialog({ org, onClose, onApprove, onRejectRequest }) {
  // Click-to-review panel (Phase 2): pulls the full context pack for one org.
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setData(null); setFailed(false);
    if (!org) return;
    api.adminVerificationReview(org.user_id).then(setData).catch(() => setFailed(true));
  }, [org]);

  const o = data?.org || org || {};
  return (
    <Dialog open={!!org} onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="verification-review-dialog" className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-forest">{o.org_name || o.name}</DialogTitle>
          <DialogDescription>
            Full review context — decide with confidence, the org is notified either way.
          </DialogDescription>
        </DialogHeader>
        {failed && <div className="text-sm text-red-600">Could not load review data.</div>}
        {!data && !failed && <div className="text-sm text-neutral-500 py-6">Loading review…</div>}
        {data && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <ReviewStat testId="review-stat-age" label="Days on platform" value={data.account_age_days ?? "—"} />
              <ReviewStat testId="review-stat-opps" label={`Opps (${data.open_opportunities} open)`} value={data.opportunities_count} />
              <ReviewStat testId="review-stat-apps" label={`Applications (${data.applications.accepted} accepted)`} value={data.applications.total} />
              <ReviewStat testId="review-stat-hours" label="Approved hours" value={data.hours.approved_total} />
            </div>

            <div className="text-sm text-neutral-700 bg-forest/5 border border-forest/10 rounded-lg px-3 py-2">
              <span className="font-medium">{o.name}</span> · <span className="font-mono text-xs">{o.email}</span>
              {o.location && <span className="text-neutral-500"> · {o.location}</span>}
            </div>

            {o.bio && <p className="text-sm text-neutral-700">{o.bio}</p>}

            {(o.verification_details?.website || o.verification_details?.registration_number || o.verification_details?.note) && (
              <div data-testid="review-evidence" className="space-y-2">
                <div className="text-[11px] uppercase tracking-[0.16em] text-forest/70 font-bold">Submitted evidence</div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {o.verification_details?.website && (
                    <a href={o.verification_details.website.startsWith("http") ? o.verification_details.website : `https://${o.verification_details.website}`}
                      target="_blank" rel="noreferrer">
                      <Badge variant="outline" className="rounded-full hover:border-forest cursor-pointer">
                        <Link2 className="w-3 h-3 mr-1" />{o.verification_details.website}
                      </Badge>
                    </a>
                  )}
                  {o.verification_details?.registration_number && (
                    <Badge variant="outline" className="rounded-full">Reg: {o.verification_details.registration_number}</Badge>
                  )}
                </div>
                {o.verification_details?.note && (
                  <div className="text-sm text-neutral-600 italic">"{o.verification_details.note}"</div>
                )}
              </div>
            )}

            {o.lat != null && o.lng != null && (
              <MapLeaflet
                testId={`review-map-${o.user_id}`}
                markers={[{ lat: o.lat, lng: o.lng, title: o.org_name || o.name, subtitle: o.location }]}
                circles={[{ lat: o.lat, lng: o.lng, radius_miles: 5, color: "#1A4331", fillOpacity: 0.08 }]}
                center={[o.lat, o.lng]}
                zoom={11}
                height={180}
                fitBounds
              />
            )}

            <div>
              <div className="text-[11px] uppercase tracking-[0.16em] text-forest/70 font-bold">
                Posted opportunities ({data.opportunities_count})
              </div>
              {data.opportunities.length === 0 ? (
                <div className="text-sm text-neutral-500 mt-1.5">None posted yet.</div>
              ) : (
                <div data-testid="review-opps-list" className="mt-2 space-y-1.5 max-h-44 overflow-y-auto pr-1">
                  {data.opportunities.slice(0, 12).map((op) => (
                    <div key={op.id} className="flex items-center justify-between gap-2 text-sm border border-neutral-200 rounded-lg px-3 py-1.5">
                      <Link to={`/opportunities/${op.id}`} target="_blank" className="text-forest hover:underline truncate">{op.title}</Link>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full shrink-0 ${op.status === "open" ? "bg-forest/10 text-forest" : "bg-neutral-100 text-neutral-500"}`}>
                        {op.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-2 justify-end pt-1">
              <Button data-testid="review-reject-btn" variant="outline"
                onClick={() => onRejectRequest(o)} className="rounded-full h-10 px-5 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">
                <XCircle className="w-4 h-4 mr-1.5" /> Reject…
              </Button>
              <Button data-testid="review-approve-btn"
                onClick={() => onApprove(o.user_id)} className="rounded-full h-10 px-5 bg-forest hover:bg-forest-hover">
                <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve & verify
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "analytics" ? "analytics" : "ops";
  const [orgs, setOrgs] = useState([]);
  const [ready, setReady] = useState(false);
  const [rejecting, setRejecting] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [vReason, setVReason] = useState("");
  const [vNote, setVNote] = useState("");

  const load = () => api.adminVerifications().then(setOrgs).catch(() => setOrgs([]));

  useEffect(() => {
    if (loading) return;
    if (!user?.is_admin) { nav("/"); return; }
    if (user.admin_pending) { setReady(true); return; } // OTP gate renders instead
    load().finally(() => setReady(true));
  }, [user, loading, nav]);

  const approve = async (id) => {
    await api.approveVerification(id);
    setOrgs((o) => o.filter((x) => x.user_id !== id));
    setReviewing(null);
    toast.success("Organisation verified — they've been notified");
  };

  const confirmReject = async () => {
    if (!vReason) return toast.error("Pick a reason");
    try {
      await api.rejectVerification(rejecting.user_id, vReason, vNote);
      setOrgs((o) => o.filter((x) => x.user_id !== rejecting.user_id));
      toast("Rejected — the organisation has been told why and invited to re-apply");
      setRejecting(null); setVReason(""); setVNote("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reject"); }
  };

  if (!ready) return <div className="max-w-3xl mx-auto px-6 py-20 text-neutral-500">Loading…</div>;

  // Phase 14 admin step-up: this session hasn't completed the email OTP yet —
  // every /admin API 403s (admin_otp_required) until it does, so gate the page.
  if (user?.admin_pending) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-16" data-testid="admin-otp-gate">
        <AdminOtpPanel />
      </div>
    );
  }

  const setTab = (t) => setSearchParams(t === "analytics" ? { tab: "analytics" } : {});

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="admin-page">
      <Seo title="Admin" />
      <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">Admin</div>
      <div className="mt-3 inline-flex rounded-full border border-neutral-200 bg-white p-1" data-testid="admin-tabs">
        <button
          data-testid="admin-tab-ops"
          onClick={() => setTab("ops")}
          className={`text-sm px-5 py-2 rounded-full inline-flex items-center gap-1.5 transition-colors ${tab === "ops" ? "bg-forest text-white" : "text-neutral-600 hover:text-forest"}`}
        >
          <ShieldCheck className="w-4 h-4" /> Operations
        </button>
        <button
          data-testid="admin-tab-analytics"
          onClick={() => setTab("analytics")}
          className={`text-sm px-5 py-2 rounded-full inline-flex items-center gap-1.5 transition-colors ${tab === "analytics" ? "bg-forest text-white" : "text-neutral-600 hover:text-forest"}`}
        >
          <BarChart3 className="w-4 h-4" /> Analytics
        </button>
      </div>

      <LaunchHealthPanel
        onFeedbackClick={() => {
          setTab("ops");
          setTimeout(() => document.querySelector('[data-testid="site-feedback-section"]')?.scrollIntoView({ behavior: "smooth" }), 150);
        }}
      />

      {tab === "analytics" ? (
        <>
          <h1 className="font-display text-4xl text-forest mt-5 tracking-tight flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-forest" /> Analytics
          </h1>
          <p className="text-neutral-600 text-sm mt-2">First-party operational insight — activity, growth, engagement, traffic and quality flags. No third-party trackers.</p>
          <AdminAnalytics />
        </>
      ) : (
        <>
      <h1 className="font-display text-4xl text-forest mt-5 tracking-tight flex items-center gap-2">
        <ShieldCheck className="w-7 h-7 text-forest" /> Verification queue
      </h1>
      <p className="text-neutral-600 text-sm mt-2">Review organisations requesting verification.</p>

      <div className="mt-8 space-y-4" data-testid="verification-queue">
        {orgs.length === 0 && <div className="text-sm text-neutral-500">No pending requests. All caught up.</div>}
        {orgs.map((o) => (
          <Card key={o.user_id} className="border-neutral-200" data-testid={`verification-${o.user_id}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <button
                    data-testid={`review-open-${o.user_id}`}
                    onClick={() => setReviewing(o)}
                    className="font-display text-xl text-forest hover:text-terracotta hover:underline underline-offset-4 transition-colors text-left"
                    title="Open full review"
                  >
                    {o.org_name || o.name}
                  </button>
                  <div className="text-sm text-neutral-600 mt-0.5">{o.location}</div>
                  <div data-testid={`verification-contact-${o.user_id}`} className="text-sm text-neutral-700 mt-2 bg-forest/5 border border-forest/10 rounded-lg px-3 py-2 inline-flex items-center gap-2">
                    <Mail className="w-3.5 h-3.5 text-terracotta shrink-0" />
                    <span>Requested by <span className="font-medium">{o.name}</span> · <span className="font-mono text-xs">{o.email}</span></span>
                  </div>
                  {o.bio && <p className="text-sm text-neutral-700 mt-2 max-w-xl">{o.bio}</p>}
                  {o.verification_details && (
                    <div className="flex flex-wrap gap-2 mt-3 text-xs">
                      {o.verification_details.website && (
                        <Badge variant="outline" className="rounded-full">{o.verification_details.website}</Badge>
                      )}
                      {o.verification_details.registration_number && (
                        <Badge variant="outline" className="rounded-full">Reg: {o.verification_details.registration_number}</Badge>
                      )}
                      {o.verification_details.source === "partner_page" && (
                        <Badge
                          data-testid={`partner-email-status-${o.user_id}`}
                          className={`rounded-full border-none ${
                            o.verification_details.confirmation_email?.status === "sent"
                              ? "bg-forest/10 text-forest"
                              : o.verification_details.confirmation_email?.status === "failed"
                                ? "bg-red-100 text-red-700"
                                : "bg-neutral-100 text-neutral-500"
                          }`}
                        >
                          <Mail className="w-3 h-3 mr-1" />
                          {o.verification_details.confirmation_email?.status === "sent"
                            ? "Confirmation sent"
                            : o.verification_details.confirmation_email?.status === "failed"
                              ? "Confirmation FAILED"
                              : "Confirmation pending"}
                        </Badge>
                      )}
                    </div>
                  )}
                  {o.verification_details?.note && (
                    <div className="text-sm text-neutral-600 mt-2 italic">"{o.verification_details.note}"</div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <Badge className="bg-amber-100 text-amber-800 border-none inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" /> Pending
                  </Badge>
                  {o.lat != null && o.lng != null ? (
                    <div style={{ width: 200 }}>
                      <MapLeaflet
                        testId={`partner-mini-map-${o.user_id}`}
                        markers={[{ lat: o.lat, lng: o.lng, title: o.org_name || o.name, subtitle: o.location }]}
                        circles={[{ lat: o.lat, lng: o.lng, radius_miles: 5, color: "#1A4331", fillOpacity: 0.08 }]}
                        center={[o.lat, o.lng]}
                        zoom={11}
                        height={200}
                        fitBounds
                      />
                      <VerifiedNearbyChip userId={o.user_id} />
                    </div>
                  ) : (
                    <div
                      data-testid={`partner-no-location-${o.user_id}`}
                      className="text-[11px] text-neutral-400 italic border border-dashed border-neutral-200 rounded-lg px-3 py-2 w-[200px] text-center"
                    >no location pin</div>
                  )}
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button data-testid={`review-btn-${o.user_id}`} onClick={() => setReviewing(o)} variant="outline" className="rounded-full h-9 px-4 text-sm border-forest/30 text-forest hover:bg-forest/5">
                  <Eye className="w-4 h-4 mr-1.5" /> Review
                </Button>
                <Button data-testid={`approve-${o.user_id}`} onClick={() => approve(o.user_id)} className="rounded-full bg-forest hover:bg-forest-hover h-9 px-4 text-sm">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve
                </Button>
                <Button data-testid={`reject-${o.user_id}`} onClick={() => setRejecting(o)} variant="outline" className="rounded-full h-9 px-4 text-sm">
                  <XCircle className="w-4 h-4 mr-1.5" /> Reject
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <VerificationReviewDialog
        org={reviewing}
        onClose={() => setReviewing(null)}
        onApprove={approve}
        onRejectRequest={(o) => { setReviewing(null); setRejecting(o); }}
      />

      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent data-testid="verify-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject {rejecting?.org_name || rejecting?.name}?</DialogTitle>
            <DialogDescription>They'll be notified with your reason and invited to re-apply once it's fixed.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={vReason} onValueChange={setVReason}>
              <SelectTrigger data-testid="verify-reject-reason" className="h-11"><SelectValue placeholder="Reason" /></SelectTrigger>
              <SelectContent>
                {VERIFY_REJECT_REASONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea data-testid="verify-reject-note" value={vNote} onChange={(e) => setVNote(e.target.value)} rows={3} placeholder="Extra detail for the organisation (optional)" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="rounded-full" onClick={() => setRejecting(null)}>Cancel</Button>
              <Button data-testid="verify-reject-confirm" onClick={confirmReject} className="rounded-full bg-red-600 hover:bg-red-700">Reject & notify</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ReportsSection />
      <RoleBadgesPanel />

      <BroadcastCard />

      <SiteFeedbackSection />

      <SkillSuggestionsSection />

      <AppealsSection />

      <AuditLogSection />
      <CollectivesQueue />
      <CollectivesHealth />
      <ReferrersQueue />
      <CampaignsPanel />
      <PerksPanel />
      <EmployerPanel />
      <AdminSearchSection />
      <OutreachSection />
      <GrowthPanel />
      <FeaturedSection />
      <FeaturedStoriesSection />
      <DigestCard />
      <ContentStudio />
        </>
      )}
    </div>
  );
}

